import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import { useTranslation, Trans } from 'react-i18next';
import { useStyles } from './AcceptRejectStyles';
import { Button } from '~/UIKit';
import { ToastVariant, useProvider, useToastMessage, useNavigation } from '~/Hooks';
import { useRequestConnectPublicApi, useRequestClientName } from '~/requests';
import classNames from 'classnames';
import { AcceptRejectCountDown } from './AcceptRejectCountDown';
import { Error } from '~/Components';
import { Route } from '~/Constants/Routes.constants';

export interface IAcceptRejectProps {
  handleAcceptClickFunc?: () => void;
  handleRejectClickFunc?: () => void;
}

export const AcceptRejectPublicApi = ({
  handleAcceptClickFunc,
  handleRejectClickFunc,
}: IAcceptRejectProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { currentProvider } = useProvider();
  const { showToast } = useToastMessage();
  const { navigate } = useNavigation();
  const { data, error, isLoading, mutate } = useRequestConnectPublicApi();
  const location = useLocation().search;
  const clientId = new URLSearchParams(location).get('clientId') as string;
  const redirectUrl = new URLSearchParams(location).get('redirectUrl') as string;
  const { data: client, error: clientError, isLoading: clientLoading } = useRequestClientName({
    clientId,
  });
  const [acceptClicked, setAcceptClicked] = useState(false);

  const handleAcceptClick = () => {
    handleAcceptClickFunc?.();
    mutate({ clientId, redirectUrl });
    setAcceptClicked(true);
  };

  useEffect(() => {
    if (data?.redirectUrl) {
      setTimeout(() => {
        window.location.replace(`${data?.redirectUrl}`);
      }, 4400);
    }
  }, [data?.redirectUrl]);

  const handleCancelClick = () => {
    handleRejectClickFunc?.();
    showToast('publicApi', t('publicApi.acceptReject.cancelToastText'), ToastVariant.INFO, true);
    navigate(Route.HOME);
  };

  return (
    <div className={classes.container} data-testid="AcceptRejectPageWrapper">
      <div className={classes.innerBox}>
        <>
          <Error error={error} />
          <Error error={clientError} />
        </>
        {!acceptClicked ? (
          <div>
            <Typography className={classNames(classes.text, classes.textPaddingBottom)}>
              <Trans
                i18nKey="publicApi.accepReject.p1"
                values={{
                  clientName: client?.clientName,
                  providerName: currentProvider?.name,
                }}
              />
            </Typography>
            <Typography className={classes.text}>{t('publicApi.acceptReject.p2')}</Typography>
            <Typography className={classes.text}>-----</Typography>
            <Typography className={classes.text}>{t('publicApi.acceptReject.p3')}</Typography>
            <Typography className={classes.text}>-----</Typography>
            <Typography className={classes.text}>{t('publicApi.acceptReject.p4')}</Typography>
            <Typography className={classes.text}>-----</Typography>
            <Typography className={classes.text}>{t('publicApi.acceptReject.p5')}</Typography>

            <div className={classes.buttons}>
              <Button
                className={classes.rejectButton}
                size="medium"
                onClick={handleCancelClick}
                data-testid="AcceptRejectCancelButton"
                disabled={acceptClicked}
              >
                <Typography className={classes.rejectButtonText}>{t('app.cancel')}</Typography>
              </Button>
              <Button
                className={classes.acceptButton}
                variant="contained"
                size="medium"
                onClick={handleAcceptClick}
                data-testid="AcceptRejectApproveButton"
                disabled={acceptClicked}
                isLoading={isLoading || clientLoading}
              >
                <Typography className={classes.acceptButtonText}>
                  {t('publicApi.acceptReject.acceptButton')}
                </Typography>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <AcceptRejectCountDown redirectUrl={data?.redirectUrl} />
          </>
        )}
      </div>
    </div>
  );
};
